/**
 * ModelPortfoliosPage — ETF-backed model portfolio library
 *
 * Lets the user browse standard model portfolios (Vanguard LS, 60/40,
 * All-Weather, Permanent, Golden Butterfly etc.) composed from real
 * LSE-listed UCITS ETFs, and apply one to a notional portfolio value
 * to see concrete £ targets per ticker.
 */
import { useEffect, useState } from 'react'
import { Layers, RefreshCw, ArrowRight, TrendingUp, Percent, Zap, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api, getHouseholdSummary } from '@/lib/api'

interface LineItem {
  etf_id: string
  ticker: string
  name: string
  asset_class: string
  weight_pct: number
  ocf_pct: number
  target_gbp?: number
}

interface Model {
  id: string
  name: string
  description: string
  category: string
  expected_return_pct: number
  expected_vol_pct: number
  weighted_ocf_pct: number
  line_items: LineItem[]
}

interface ListResponse {
  models: Model[]
  by_category: Record<string, Model[]>
}

interface ApplyResponse {
  model_id: string
  model_name: string
  portfolio_value_gbp: number
  weighted_ocf_pct: number
  expected_return_pct: number
  expected_vol_pct: number
  target_positions: LineItem[]
  annual_fees_gbp: number
}

// Negatives render as -£1,234 (not the broken "£-1,234")
const fmt = (n: number) =>
  `${n < 0 ? '-' : ''}£${Math.abs(n ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const CATEGORY_COLOURS: Record<string, string> = {
  'Balanced':       'border-blue/30 bg-blue/5',
  'Risk Parity':    'border-purple/30 bg-purple/5',
  'Low Volatility': 'border-emerald/30 bg-emerald/5',
  'Aggressive':     'border-rose/30 bg-rose/5',
}

interface PlanTrade {
  action: 'BUY' | 'SELL'
  ticker?: string
  name?: string
  account_name?: string
  account_id?: number
  value_gbp: number
  reasoning?: string
}

interface PlanResponse {
  model: Model
  target_asset_classes: Record<string, number>
  mapping_warnings: string[]
  plan: {
    current_allocation?: Record<string, number>
    target_allocation?: Record<string, number>
    gaps?: any[]
    trades: PlanTrade[]
    total_buys_gbp?: number
    total_sells_gbp?: number
    net_gbp?: number
    tax_warnings?: any[]
    summary_text?: string
  }
}

export default function ModelPortfoliosPage() {
  const [data, setData] = useState<ListResponse | null>(null)
  const [applied, setApplied] = useState<ApplyResponse | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [portfolioValue, setPortfolioValue] = useState(500_000)
  // 'live' = fetched from /api/accounts/summary; 'unavailable' = fetch failed,
  // slider is a manual value, NOT the real pot. Never claim a stale hardcode.
  const [potSource, setPotSource] = useState<'loading' | 'live' | 'unavailable'>('loading')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [planResult, setPlanResult] = useState<PlanResponse | null>(null)
  const [planLoading, setPlanLoading] = useState(false)
  const [planError, setPlanError] = useState<string | null>(null)

  const fetchList = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<ListResponse>('/api/models/list')
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load models')
    } finally {
      setLoading(false)
    }
  }

  // Real discretionary pot from the household summary — no hardcoded figure.
  const fetchDiscretionary = async () => {
    try {
      const summary = await getHouseholdSummary()
      const disc = summary?.discretionary_value
      if (typeof disc === 'number' && Number.isFinite(disc) && disc > 0) {
        setPortfolioValue(Math.round(disc))
        setPotSource('live')
      } else {
        setPotSource('unavailable')
      }
    } catch {
      setPotSource('unavailable')
    }
  }

  const applyModel = async (modelId: string) => {
    setSelectedId(modelId)
    setPlanResult(null)
    setPlanError(null)
    setApplyError(null)
    try {
      const res = await api.post<ApplyResponse>('/api/models/apply', {
        model_id: modelId,
        portfolio_value_gbp: portfolioValue,
      })
      setApplied(res.data)
    } catch (err: any) {
      setApplied(null)
      setApplyError(
        err?.response?.data?.detail ?? err?.message ?? 'Failed to apply model',
      )
    }
  }

  const generatePlan = async () => {
    if (!selectedId) return
    setPlanLoading(true)
    setPlanError(null)
    try {
      const res = await api.post<PlanResponse>('/api/models/plan', {
        model_id: selectedId,
        include_non_discretionary: false,
        min_trade_gbp: 100,
      })
      setPlanResult(res.data)
    } catch (err: any) {
      setPlanError(err?.message ?? 'Failed to generate plan')
    } finally {
      setPlanLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    fetchDiscretionary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedId) applyModel(selectedId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioValue])

  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="skeleton h-8 w-64" />
        <div className="skeleton h-64" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error ?? 'No data'}</p>
        <button type="button" onClick={fetchList} className="btn-secondary mt-3">Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Layers size={20} className="text-blue-light" aria-hidden />
            Model Portfolio Library
          </h1>
          <p className="text-sm text-text-secondary">
            ETF-backed model portfolios — LSE-listed, UCITS-compliant, applied to your discretionary pot
          </p>
        </div>
        <button type="button" onClick={fetchList} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {/* Portfolio value control */}
      <div className="glass rounded-xl p-4">
        <label className="panel-title block mb-1">
          Apply to portfolio value
        </label>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="range"
            min={50_000}
            max={3_000_000}
            step={10_000}
            value={portfolioValue}
            onChange={(e) => setPortfolioValue(parseFloat(e.target.value))}
            className="flex-1 accent-blue min-w-[200px]"
            aria-label="Portfolio value"
          />
          <span className="stat-hero text-text-primary font-mono">{fmt(portfolioValue)}</span>
        </div>
        <p className="text-[10px] text-text-muted mt-2">
          {potSource === 'live' && 'Loaded from your accounts: current total discretionary pot. Adjust to see how a model applies at different sizes.'}
          {potSource === 'loading' && 'Loading your live discretionary total…'}
          {potSource === 'unavailable' && 'Could not load your live discretionary total — this slider is a manual value, not your real pot.'}
        </p>
      </div>

      {/* Model grid */}
      {Object.entries(data.by_category).map(([cat, models]) => (
        <section key={cat}>
          <h2 className="panel-title mb-3">{cat}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 stagger-children">
            {models.map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => applyModel(m.id)}
                className={clsx(
                  'card hover-lift p-4 border-2 text-left transition-all',
                  CATEGORY_COLOURS[cat] ?? 'border-border',
                  selectedId === m.id && 'ring-2 ring-blue shadow-lg shadow-blue/20',
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-sm font-bold text-text-primary">{m.name}</h3>
                  <span className="badge badge-blue text-[9px] shrink-0">{m.line_items.length} ETFs</span>
                </div>
                <p className="text-[11px] text-text-secondary mb-3 leading-snug">{m.description}</p>

                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <div>
                    <p className="text-text-muted flex items-center gap-1">
                      <TrendingUp size={9} aria-hidden /> Return
                    </p>
                    <p className="font-bold text-emerald-light">{m.expected_return_pct}%</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Vol</p>
                    <p className="font-bold text-amber">{m.expected_vol_pct}%</p>
                  </div>
                  <div>
                    <p className="text-text-muted flex items-center gap-1">
                      <Percent size={9} aria-hidden /> OCF
                    </p>
                    <p className="font-bold text-text-primary">{(m.weighted_ocf_pct ?? 0).toFixed(2)}%</p>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-border/50 text-[10px] space-y-0.5">
                  {m.line_items.slice(0, 4).map(l => (
                    <div key={l.etf_id} className="flex items-center justify-between text-text-muted">
                      <span className="font-mono">{l.ticker}</span>
                      <span>{(l.weight_pct ?? 0).toFixed(0)}%</span>
                    </div>
                  ))}
                  {m.line_items.length > 4 && (
                    <div className="text-text-muted">+ {m.line_items.length - 4} more</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}

      {/* Apply error */}
      {applyError && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-sm text-rose-light">
          Failed to apply model: {applyError}
        </div>
      )}

      {/* Applied model detail */}
      {applied && (
        <section className="card p-5 border-2 border-blue/40 bg-blue/5">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-text-muted">Applied model</p>
              <h2 className="text-lg font-bold text-text-primary">{applied.model_name}</h2>
              <p className="text-xs text-text-muted mt-1">
                Applied to {fmt(applied.portfolio_value_gbp)} · expected {applied.expected_return_pct}% return ·{' '}
                vol {applied.expected_vol_pct}% · OCF {(applied.weighted_ocf_pct ?? 0).toFixed(2)}% ({fmt(applied.annual_fees_gbp)}/yr fees)
              </p>
            </div>
          </div>

          <div className="card p-3 bg-surface-DEFAULT/50 table-responsive">
            <table className="table-pro w-full text-xs">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Name</th>
                  <th>Asset Class</th>
                  <th className="!text-right">Weight</th>
                  <th className="!text-right">OCF</th>
                  <th className="!text-right">Target £</th>
                </tr>
              </thead>
              <tbody>
                {applied.target_positions.map(p => (
                  <tr key={p.etf_id}>
                    <td className="font-mono font-semibold text-blue-light">{p.ticker}</td>
                    <td className="text-text-primary">{p.name}</td>
                    <td className="text-text-muted">{p.asset_class}</td>
                    <td className="text-right font-mono text-text-primary">{(p.weight_pct ?? 0).toFixed(1)}%</td>
                    <td className="text-right font-mono text-text-muted">{(p.ocf_pct ?? 0).toFixed(2)}%</td>
                    <td className="text-right font-mono font-bold text-text-primary">
                      {fmt(p.target_gbp ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Generate Plan button */}
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={generatePlan}
              disabled={planLoading}
              className="btn-primary gap-2"
            >
              {planLoading
                ? <Loader2 size={14} className="animate-spin" aria-hidden />
                : <Zap size={14} aria-hidden />
              }
              {planLoading ? 'Generating trade plan...' : 'Generate Rebalance Plan'}
            </button>
            <p className="text-[11px] text-text-muted">
              Converts ETF targets → asset classes → concrete trades across your discretionary accounts.
            </p>
          </div>
        </section>
      )}

      {/* Plan results */}
      {planError && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-sm text-rose-light">
          {planError}
        </div>
      )}

      {planResult && (
        <section className="card p-5 border-2 border-emerald/30 bg-emerald/5">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-text-muted">Automated rebalance plan</p>
              <h2 className="text-lg font-bold text-text-primary">
                {planResult.plan.trades.length} trades generated
              </h2>
              {planResult.plan.summary_text && (
                <p className="text-xs text-text-secondary mt-1">{planResult.plan.summary_text}</p>
              )}
            </div>
            <div className="text-right shrink-0 text-xs">
              {planResult.plan.total_buys_gbp !== undefined && (
                <p className="text-emerald-light font-bold">
                  Buys: {fmt(planResult.plan.total_buys_gbp)}
                </p>
              )}
              {planResult.plan.total_sells_gbp !== undefined && (
                <p className="text-rose-light font-bold">
                  Sells: {fmt(planResult.plan.total_sells_gbp)}
                </p>
              )}
              {planResult.plan.net_gbp !== undefined && (
                <p className="text-text-muted">Net: {fmt(planResult.plan.net_gbp)}</p>
              )}
            </div>
          </div>

          {/* Target asset classes */}
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-text-primary uppercase tracking-wider mb-2">
              Target asset classes (mapped from ETF library)
            </h3>
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(planResult.target_asset_classes).map(([k, v]) => (
                <span key={k} className="badge badge-blue text-[11px]">
                  {k}: {v.toFixed(1)}%
                </span>
              ))}
            </div>
          </div>

          {/* Mapping warnings */}
          {planResult.mapping_warnings.length > 0 && (
            <div className="card p-3 bg-amber/5 border-amber/20 mb-4">
              <p className="text-[11px] text-amber-light font-semibold mb-1">Mapping notes</p>
              <ul className="text-[11px] text-text-secondary space-y-0.5 list-disc list-inside">
                {planResult.mapping_warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Trade list */}
          <div className="card p-3 bg-surface-DEFAULT/50">
            <div className="table-responsive">
              <table className="table-pro w-full text-xs">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Ticker / Name</th>
                    <th>Account</th>
                    <th className="!text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {planResult.plan.trades.map((t, i) => (
                    <tr key={i}>
                      <td>
                        <span className={clsx(
                          'badge text-[10px] font-bold',
                          t.action === 'BUY' ? 'badge-green' : 'badge-red',
                        )}>
                          {t.action}
                        </span>
                      </td>
                      <td className="font-mono text-text-primary">
                        {t.ticker ?? t.name ?? '—'}
                      </td>
                      <td className="text-text-muted text-[11px]">{t.account_name ?? ''}</td>
                      <td className="text-right font-mono font-bold text-text-primary">
                        {fmt(t.value_gbp)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {planResult.plan.tax_warnings && planResult.plan.tax_warnings.length > 0 && (
            <div className="card p-3 bg-amber/5 border-amber/20 mt-3">
              <p className="text-[11px] text-amber-light font-semibold mb-1">
                Tax warnings ({planResult.plan.tax_warnings.length})
              </p>
              <p className="text-[11px] text-text-secondary">
                Some SELL trades may crystallise capital gains. Review in the Tax-Loss Harvesting
                page or Rebalancer for per-trade detail.
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
